/**
 * Chore rows → domain entities → everything the phone's chore screen renders.
 *
 * Nothing is computed here that `@office/domain/chores` already computes.
 * Rotation, streaks, grace, escalation, swap eligibility and the overdue
 * settlement are all imported; this file's whole job is to feed them the rows
 * from the local mirror and shape the answers for a screen.
 *
 * In particular the *displayed* status of a past chore comes from
 * `settleOverdue`, not from the stored column. Status is derived, never stored
 * (docs/01 §3.3) — so a `pending` row from three days ago renders as `missed`
 * or `graced` according to the same grace ledger the streak uses, even if the
 * rollover job has not reached this device yet.
 */

import { useCallback, useMemo, useState } from 'react'
import type { OfficeDb } from '../../lib/db.js'
import { mutations } from '../../lib/mutations.js'
import {
  acceptSwap as domainAcceptSwap,
  requestSwap as domainRequestSwap,
  computeStreak,
  escalationFor,
  officeWeekOf,
  settleOverdue,
} from '@office/domain/chores'
import {
  daysBetween,
  daysOfWeek,
  rolloverDayOf,
  type ChoreDefinition,
  type ChoreInstance,
  type ChoreStatus,
  type ChoreSwap,
  type ChoreDefId,
  type ChoreInstanceId,
  type DayKey,
  type MemberId,
  type StreakState,
  type SwapId,
  type SwapStatus,
  type TrustedClock,
  type WeekKey,
} from '@office/domain'
import {
  asBool,
  asIdOrNull,
  asMs,
  asNum,
  asStringList,
  asText,
  asTextOrNull,
  column,
  nameOf,
  nameOrYou,
  useMembers,
  useMutationContext,
  useNow,
  useSettings,
  useTable,
  type MemberLite,
  type Row,
  type Tone,
} from './live.js'

// ---------------------------------------------------------------------------
// Row adapters
// ---------------------------------------------------------------------------

const CHORE_STATUSES: readonly ChoreStatus[] = ['pending', 'done', 'missed', 'graced', 'swapped']
const SWAP_STATUSES: readonly SwapStatus[] = ['pending', 'accepted', 'declined', 'cancelled']

export function asChoreStatus(v: unknown): ChoreStatus {
  const s = asText(v)
  return (CHORE_STATUSES as readonly string[]).includes(s) ? (s as ChoreStatus) : 'pending'
}

export function asSwapStatus(v: unknown): SwapStatus {
  const s = asText(v)
  return (SWAP_STATUSES as readonly string[]).includes(s) ? (s as SwapStatus) : 'pending'
}

function asEscalation(v: unknown): 0 | 1 | 2 {
  const n = Math.trunc(asNum(v, 0))
  return n >= 2 ? 2 : n === 1 ? 1 : 0
}

export function toChoreDefinition(row: Row): ChoreDefinition {
  const cadence = asText(row['cadence'], 'daily') === 'weekly' ? 'weekly' : 'daily'
  const weekday = column(row, 'weekday')
  return {
    id: asText(row['id']) as ChoreDefId,
    title: asText(row['title'], 'Chore'),
    description: asTextOrNull(row['description']),
    cadence,
    weekday: weekday === null || weekday === undefined || weekday === '' ? null : asNum(weekday, 0),
    rotationEnabled: asBool(column(row, 'rotation_enabled'), true),
    rotationPool: asStringList(column(row, 'rotation_pool')) as MemberId[],
    rotationOffset: asNum(column(row, 'rotation_offset'), 0),
    fixedAssigneeId: asIdOrNull<MemberId>(column(row, 'fixed_assignee_id')),
    pausedInRamadan: asBool(column(row, 'paused_in_ramadan')),
    active: asBool(row['active'], true),
    createdAt: asMs(column(row, 'created_at')) ?? 0,
  }
}

/**
 * Is this a day the office can actually reckon with?
 *
 * Everything downstream — grace, escalation, the week strip — is `daysBetween`
 * arithmetic on a DayKey, and `daysBetween` on a blank or malformed one returns
 * NaN. NaN then fails every `<= 0` guard in `escalationFor`, so a row with a
 * missing `due_day` came out as level 2: the loudest state the system has,
 * "N days late" rendered as `NaN days late`, on a chore nobody can date. A row
 * we cannot place in time is not overdue; it is unreadable, and it is left out.
 */
export function isDayKey(v: unknown): boolean {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
  const [y, m, d] = v.split('-').map(Number) as [number, number, number]
  if (m < 1 || m > 12 || d < 1 || d > 31) return false
  const probe = new Date(Date.UTC(y, m - 1, d))
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d
}

export function toChoreInstance(row: Row): ChoreInstance {
  return {
    id: asText(row['id']) as ChoreInstanceId,
    definitionId: asText(column(row, 'definition_id')) as ChoreDefId,
    dueDay: asText(column(row, 'due_day')) as DayKey,
    assigneeId: asText(column(row, 'assignee_id')) as MemberId,
    status: asChoreStatus(row['status']),
    completedAt: asMs(column(row, 'completed_at')),
    completedBy: asIdOrNull<MemberId>(column(row, 'completed_by')),
    escalationLevel: asEscalation(column(row, 'escalation_level')),
  }
}

export function toChoreSwap(row: Row): ChoreSwap {
  return {
    id: asText(row['id']) as SwapId,
    instanceId: asText(column(row, 'instance_id')) as ChoreInstanceId,
    fromMemberId: asText(column(row, 'from_member', 'from_member_id')) as MemberId,
    /** Null = broadcast: first eligible person to accept takes it (docs/01 §3.2). */
    toMemberId: asIdOrNull<MemberId>(column(row, 'to_member', 'to_member_id')),
    status: asSwapStatus(row['status']),
    reason: asTextOrNull(row['reason']),
    createdAt: asMs(column(row, 'created_at')) ?? 0,
    resolvedAt: asMs(column(row, 'resolved_at')),
    resolvedBy: asIdOrNull<MemberId>(column(row, 'resolved_by')),
  }
}

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

export interface ChoreItem {
  instance: ChoreInstance
  title: string
  description: string | null
  /** "You" for the phone's owner. */
  assigneeName: string
  mine: boolean
  done: boolean
  /** 0 on time, 1 one day overdue, 2 two or more (docs/01 §3.4). */
  escalation: 0 | 1 | 2
  daysLate: number
  /** Never 'danger'. Overdue chores are amber. */
  tone: Tone
  /** True when the domain would accept a swap request from the viewer. */
  canSwap: boolean
  /** A swap request is already outstanding on this chore. */
  swapPending: boolean
  /**
   * Who this chore may actually be handed to — the definition's rotation pool
   * where it has one, otherwise every rotation-eligible member, always minus
   * whoever currently holds it. "Broadcasts to everyone eligible" (docs/01
   * §3.2) is a smaller set than "everyone", and offering a name outside the
   * pool queues a swap the rotation will never honour.
   */
  swapCandidates: readonly MemberLite[]
}

export interface IncomingSwap {
  swap: ChoreSwap
  item: ChoreItem
  fromName: string
  /** Broadcast swaps are open to everyone eligible; first to accept takes it. */
  broadcast: boolean
}

export interface WeekDay {
  day: DayKey
  isToday: boolean
  isPast: boolean
  items: ChoreItem[]
}

export interface ChoreBoard {
  /** Corrected office time. Never `Date.now()`. */
  now: number
  today: DayKey
  week: WeekKey
  members: readonly MemberLite[]
  todayItems: ChoreItem[]
  weekDays: WeekDay[]
  /** The viewer's own unfinished past chores. Nagging is private (docs/01 §3.4). */
  myOverdue: ChoreItem[]
  streak: StreakState
  incoming: IncomingSwap[]
  myTodayTotal: number
  myTodayDone: number
  /** True when the viewer had something due today and finished all of it. */
  allDoneToday: boolean
}

export interface ChoreActions {
  /** Tap to complete, tap again to undo. */
  toggle(item: ChoreItem): Promise<void>
  requestSwap(instance: ChoreInstance, to: MemberId | null, reason: string | null): Promise<void>
  acceptSwap(swap: ChoreSwap): Promise<void>
}

export interface UseChoreBoardOptions {
  /** Clock refresh interval. 0 freezes it, which is what an injected-clock test wants. */
  nowIntervalMs?: number | undefined
}

export function overdueLabel(daysLate: number): string {
  if (!Number.isFinite(daysLate) || daysLate <= 0) return ''
  return daysLate === 1 ? '1 day late' : `${daysLate} days late`
}

function toneFor(instance: ChoreInstance, escalation: 0 | 1 | 2): Tone {
  if (instance.status === 'done') return 'good'
  // AMBER, never red (docs/01 §3.4). Red is for genuine errors only.
  return escalation > 0 ? 'warn' : 'neutral'
}

/**
 * The whole chore screen's data, derived on every render from the mirror.
 *
 * Optimistic state note: `mutations.completeChore` / `uncompleteChore` write
 * the local mirror, so a tap is visible immediately through `liveQuery`.
 * `requestSwap` and `acceptSwap` only append to the outbox — the swap row is
 * minted by the server — so those two are acknowledged from React state until
 * a sync brings the row back. That is why `pendingRequests` and
 * `acceptedSwaps` exist rather than a write to Dexie: components never write
 * to the mirror directly.
 */
export function useChoreBoard(
  db: OfficeDb,
  clock: TrustedClock,
  me: MemberId,
  opts: UseChoreBoardOptions = {},
): { board: ChoreBoard; actions: ChoreActions } {
  const now = useNow(clock, opts.nowIntervalMs ?? 30_000)
  const settings = useSettings(db)
  const members = useMembers(db)
  const ctx = useMutationContext(db, clock, me)

  const defRows = useTable(db, 'chore_definitions')
  const instRows = useTable(db, 'chore_instances')
  const swapRows = useTable(db, 'chore_swaps')

  const [pendingRequests, setPendingRequests] = useState<readonly string[]>([])
  const [acceptedSwaps, setAcceptedSwaps] = useState<readonly string[]>([])

  const board = useMemo<ChoreBoard>(() => {
    const streakSettings = { weekStartDow: settings.weekStartDow, graceDaysPerWeek: 1 }
    const today = rolloverDayOf(now, settings.dayRolloverHour)
    // The week start is configurable (docs/01 §3.3); `weekOf` is hard-wired to
    // Saturday. Using it here made the strip and the grace ledger — which does
    // go through `officeWeekOf` — disagree about which week a day was in the
    // moment anyone changed the setting.
    const week = officeWeekOf(today, settings.weekStartDow)

    const definitions = new Map<ChoreDefId, ChoreDefinition>()
    for (const r of defRows) {
      const d = toChoreDefinition(r)
      definitions.set(d.id, d)
    }

    const raw = instRows.map(toChoreInstance).filter(i => isDayKey(i.dueDay))
    // Derived, never stored: a stale `pending` row from last week renders as the
    // `missed` or `graced` the grace ledger says it is.
    const instances = settleOverdue(raw, today, streakSettings)
    const swaps = swapRows.map(toChoreSwap)
    const openRequests = swaps.filter(s => s.status === 'pending')

    const swapPendingIds = new Set<string>(openRequests.map(s => String(s.instanceId)))
    for (const id of pendingRequests) swapPendingIds.add(id)

    /**
     * Who may take this chore. The definition's rotation pool is the answer
     * where it has one — that is what "everyone eligible" means for a chore
     * that rotates — otherwise every rotation-eligible member. The person
     * currently holding it is never a candidate for taking it from themselves.
     */
    const eligibleFor = (instance: ChoreInstance): MemberLite[] => {
      const def = definitions.get(instance.definitionId)
      const pool = def?.rotationEnabled === true ? def.rotationPool : []
      const inPool =
        pool.length > 0
          ? members.filter(m => (pool as readonly string[]).includes(String(m.id)))
          : members.filter(m => m.rotationEligible)
      return inPool.filter(m => m.id !== instance.assigneeId)
    }

    const toItem = (instance: ChoreInstance): ChoreItem => {
      const def = definitions.get(instance.definitionId)
      const escalation = escalationFor(instance, today)
      const daysLate = Math.max(0, daysBetween(instance.dueDay, today))
      const swapPending = swapPendingIds.has(String(instance.id))
      const swapCandidates = eligibleFor(instance)
      // Eligibility is the domain's answer, not a re-implementation of it.
      const dryRun = domainRequestSwap({
        instance,
        fromMemberId: me,
        toMemberId: null,
        now,
        id: 'dry-run' as SwapId,
        existingSwaps: openRequests,
      })
      return {
        instance,
        title: def?.title ?? 'Chore',
        description: def?.description ?? null,
        assigneeName: nameOrYou(members, instance.assigneeId, me),
        mine: instance.assigneeId === me,
        done: instance.status === 'done',
        escalation,
        daysLate: escalation === 0 ? 0 : daysLate,
        tone: toneFor(instance, escalation),
        canSwap: dryRun.ok && !swapPending,
        swapPending,
        swapCandidates,
      }
    }

    const byTitle = (a: ChoreItem, b: ChoreItem): number =>
      a.title.localeCompare(b.title) || String(a.instance.id).localeCompare(String(b.instance.id))
    const mineFirst = (a: ChoreItem, b: ChoreItem): number =>
      Number(b.mine) - Number(a.mine) || byTitle(a, b)

    const items = instances.map(toItem)
    const onDay = (day: DayKey): ChoreItem[] =>
      items.filter(i => i.instance.dueDay === day).sort(mineFirst)

    const todayItems = onDay(today)
    const weekDays: WeekDay[] = daysOfWeek(week).map(day => ({
      day,
      isToday: day === today,
      isPast: daysBetween(day, today) > 0,
      items: onDay(day),
    }))

    const myOverdue = items
      // daysBetween(b, a) is a − b, so this is oldest-first.
      .filter(i => i.mine && i.escalation > 0)
      .sort((a, b) => daysBetween(b.instance.dueDay, a.instance.dueDay) || byTitle(a, b))

    const incoming: IncomingSwap[] = []
    for (const swap of openRequests) {
      if (acceptedSwaps.includes(String(swap.id))) continue
      const item = items.find(i => i.instance.id === swap.instanceId)
      if (item === undefined) continue
      // Only offer what the domain would actually accept — including the
      // rotation pool, so a broadcast on a chore I am not in the pool for is
      // not offered to me at all rather than accepted and then bounced.
      const attempt = domainAcceptSwap({
        swap,
        instance: item.instance,
        acceptorId: me,
        now,
        eligible: item.swapCandidates.map(c => c.id),
      })
      if (!attempt.ok) continue
      incoming.push({
        swap,
        item,
        fromName: nameOf(members, swap.fromMemberId),
        broadcast: swap.toMemberId === null,
      })
    }
    incoming.sort((a, b) => a.swap.createdAt - b.swap.createdAt)

    const mineToday = todayItems.filter(i => i.mine)
    const myTodayDone = mineToday.filter(i => i.done).length

    return {
      now,
      today,
      week,
      members,
      todayItems,
      weekDays,
      myOverdue,
      streak: computeStreak(me, instances, today, streakSettings),
      incoming,
      myTodayTotal: mineToday.length,
      myTodayDone,
      allDoneToday: mineToday.length > 0 && myTodayDone === mineToday.length,
    }
  }, [defRows, instRows, swapRows, members, settings, me, now, pendingRequests, acceptedSwaps])

  const toggle = useCallback(
    async (item: ChoreItem) => {
      const id = String(item.instance.id)
      if (item.done) await mutations.uncompleteChore(ctx, id)
      else await mutations.completeChore(ctx, id)
    },
    [ctx],
  )

  const requestSwap = useCallback(
    async (instance: ChoreInstance, to: MemberId | null, reason: string | null) => {
      await mutations.requestSwap(ctx, String(instance.id), to === null ? null : String(to), reason)
      setPendingRequests(prev =>
        prev.includes(String(instance.id)) ? prev : [...prev, String(instance.id)],
      )
    },
    [ctx],
  )

  const acceptSwap = useCallback(
    async (swap: ChoreSwap) => {
      await mutations.acceptSwap(ctx, String(swap.id))
      setAcceptedSwaps(prev => (prev.includes(String(swap.id)) ? prev : [...prev, String(swap.id)]))
    },
    [ctx],
  )

  return { board, actions: { toggle, requestSwap, acceptSwap } }
}

/** Short, friendly day label: "Today", "Tomorrow", "Sat 5 Sep". */
export function dayLabel(day: DayKey, today: DayKey): string {
  const delta = daysBetween(today, day)
  if (delta === 0) return 'Today'
  if (delta === 1) return 'Tomorrow'
  if (delta === -1) return 'Yesterday'
  return formatDay(day)
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * 'Sat 5 Sep' from a DayKey, without touching `Intl` or the host timezone.
 * A DayKey is already the office's own calendar day; re-parsing it through a
 * local `Date` would shift it on any device west of Cairo.
 */
export function formatDay(day: DayKey): string {
  const parts = day.split('-')
  const y = Number(parts[0])
  const m = Number(parts[1])
  const d = Number(parts[2])
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(day)
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return `${DOW[dow] ?? ''} ${d} ${MON[m - 1] ?? ''}`.trim()
}
